import { useState } from "react";

interface MarketplaceProduct {
  id: number;
  name: string;
  price: number;
  minOrder: number;
  image: string;
  category: string;
  seller: string;
  sellerFlag: string;
  sellerCountry: string;
  factoryAddress: string;
  sold: number;
  commission: number;
  stock: number;
  description: string;
  rating: number;
  reviews: number;
}

interface Props {
  product: MarketplaceProduct;
  onClose: () => void;
}

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming",
];

function calcShipping(qty: number, state: string): { rate: number; service: string; days: string } {
  const west = ["California","Oregon","Washington","Nevada","Arizona"];
  const northeast = ["New York","New Jersey","Massachusetts","Connecticut","Pennsylvania"];
  let base = 9.99;
  if (west.includes(state)) base = 7.99;
  else if (northeast.includes(state)) base = 9.49;
  const rate = parseFloat((base + Math.max(0, (qty - 1) * 0.5)).toFixed(2));
  const service = west.includes(state) || northeast.includes(state) ? "UPS 2nd Day Air" : "UPS Ground";
  const days = west.includes(state) || northeast.includes(state) ? "2 business days" : "3–5 business days";
  return { rate, service, days };
}

export default function MarketplaceOrderModal({ product, onClose }: Props) {
  const [step, setStep] = useState<"details" | "shipping" | "confirm" | "success" | "received">("details");
  const [qty, setQty] = useState(product.minOrder);
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("California");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [payMethod, setPayMethod] = useState<"card" | "paypal">("card");
  const [cardNum, setCardNum] = useState("");
  const [cardExp, setCardExp] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [error, setError] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [receiptConfirmed, setReceiptConfirmed] = useState(false);

  const shipping = calcShipping(qty, state);
  const subtotal = parseFloat((product.price * qty).toFixed(2));
  const total = parseFloat((subtotal + shipping.rate).toFixed(2));

  const handleShippingNext = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!fullName || !address || !city || !zip || !phone) {
      setError("Please fill in all required fields.");
      return;
    }
    setStep("confirm");
  };

  const handlePlaceOrder = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (payMethod === "card" && (!cardNum || !cardExp || !cardCvv)) {
      setError("Please enter your card details.");
      return;
    }
    const tracking = `1Z${Math.random().toString(36).substring(2, 9).toUpperCase()}${Math.floor(Math.random() * 9000 + 1000)}`;
    setTrackingNumber(tracking);
    setStep("success");
  };

  const handleConfirmReceipt = () => {
    setReceiptConfirmed(true);
    setStep("received");
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step !== "details" && step !== "success" && step !== "received" && (
              <button
                onClick={() => setStep(step === "confirm" ? "shipping" : "details")}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
            )}
            <h2 className="font-extrabold text-gray-900 text-base">
              {step === "details" && "Sample Order"}
              {step === "shipping" && "Shipping Info"}
              {step === "confirm" && "Confirm & Pay"}
              {step === "success" && "Order Confirmed!"}
              {step === "received" && "Payment Released!"}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Step indicator */}
        {step !== "success" && step !== "received" && (
          <div className="flex items-center px-6 py-3 gap-2">
            {["details", "shipping", "confirm"].map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  step === s ? "bg-amber-400 text-white" :
                  ["details","shipping","confirm"].indexOf(step) > i ? "bg-green-400 text-white" : "bg-gray-100 text-gray-400"
                }`}>
                  {["details","shipping","confirm"].indexOf(step) > i ? <i className="ri-check-line text-xs"></i> : i + 1}
                </div>
                <span className={`text-xs font-semibold capitalize ${step === s ? "text-amber-500" : "text-gray-400"}`}>{s}</span>
                {i < 2 && <div className="flex-1 h-px bg-gray-200"></div>}
              </div>
            ))}
          </div>
        )}

        <div className="px-6 pb-6">
          {/* Step 1 */}
          {step === "details" && (
            <div className="mt-3">
              <div className="flex gap-4 mb-4">
                <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs bg-amber-50 text-amber-600 font-semibold px-2 py-0.5 rounded-full">{product.category}</span>
                  <h3 className="font-bold text-gray-900 text-sm mt-1.5 mb-1 line-clamp-2">{product.name}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{product.description}</p>
                </div>
              </div>

              {/* Factory address */}
              <div className="bg-amber-50 rounded-xl p-3 mb-4">
                <div className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-amber-500 mt-0.5 shrink-0"></i>
                  <div>
                    <p className="text-xs font-bold text-amber-700">{product.sellerFlag} {product.seller}</p>
                    <p className="text-xs text-amber-600">{product.factoryAddress}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Ships from {product.sellerCountry} → Overseas Warehouse → Your Door</p>
                  </div>
                </div>
              </div>

              {/* Quantity */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-sm font-semibold text-gray-700">Quantity</span>
                  <p className="text-xs text-gray-400">Min. order: {product.minOrder} pc{product.minOrder > 1 ? "s" : ""}</p>
                </div>
                <div className="flex items-center border border-gray-200 rounded-full overflow-hidden bg-white">
                  <button
                    onClick={() => setQty(Math.max(product.minOrder, qty - 1))}
                    className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors text-lg"
                  >-</button>
                  <span className="px-5 text-sm font-bold text-gray-800">{qty}</span>
                  <button
                    onClick={() => setQty(Math.min(product.stock, qty + 1))}
                    className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors text-lg"
                  >+</button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Unit Price</span>
                  <span className="font-semibold">${product.price}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Subtotal ({qty} pcs)</span>
                  <span className="font-semibold">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-400 mb-3">
                  <span>UPS Shipping</span>
                  <span>Calculated next step</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between">
                  <span className="font-bold text-gray-800">Est. Total</span>
                  <span className="font-extrabold text-amber-500 text-lg">${subtotal.toFixed(2)}+</span>
                </div>
              </div>

              <button
                onClick={() => setStep("shipping")}
                className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue to Shipping <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          )}

          {/* Step 2 */}
          {step === "shipping" && (
            <form onSubmit={handleShippingNext} className="mt-3 flex flex-col gap-3.5">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name *</label>
                <input required value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Street Address *</label>
                <input required value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main Street" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">City *</label>
                  <input required value={city} onChange={e => setCity(e.target.value)} placeholder="Los Angeles" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">ZIP Code *</label>
                  <input required value={zip} onChange={e => setZip(e.target.value)} placeholder="90001" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">State *</label>
                <select value={state} onChange={e => setState(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white">
                  {US_STATES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone Number *</label>
                <input required type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-truck-line text-amber-500"></i>
                  <span className="text-sm font-bold text-amber-700">UPS Shipping Estimate</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">{shipping.service}</span>
                  <span className="font-bold text-amber-600">${shipping.rate}</span>
                </div>
                <p className="text-xs text-gray-500">Estimated delivery: {shipping.days} to {state}</p>
              </div>
              {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
              <button type="submit" className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                Continue to Payment <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </form>
          )}

          {/* Step 3 */}
          {step === "confirm" && (
            <form onSubmit={handlePlaceOrder} className="mt-3">
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Order Summary</h4>
                <div className="flex gap-3 mb-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 line-clamp-2">{product.name}</p>
                    <p className="text-xs text-gray-400">Qty: {qty} × ${product.price}</p>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-3 flex flex-col gap-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-semibold">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">UPS {shipping.service}</span>
                    <span className="font-semibold">${shipping.rate}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t border-gray-200 mt-1">
                    <span>Total</span>
                    <span className="text-amber-500 text-base">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Delivery To</h4>
                <p className="text-sm font-semibold text-gray-800">{fullName}</p>
                <p className="text-sm text-gray-600">{address}, {city}, {state} {zip}</p>
                <p className="text-sm text-gray-500">{phone}</p>
              </div>

              <div className="mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Payment Method</h4>
                <div className="flex gap-2 mb-3">
                  <button type="button" onClick={() => setPayMethod("card")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all ${payMethod === "card" ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500"}`}>
                    <i className="ri-bank-card-line"></i> Credit Card
                  </button>
                  <button type="button" onClick={() => setPayMethod("paypal")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all ${payMethod === "paypal" ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500"}`}>
                    <i className="ri-paypal-line"></i> PayPal
                  </button>
                </div>
                {payMethod === "card" && (
                  <div className="flex flex-col gap-3">
                    <input value={cardNum} onChange={e => setCardNum(e.target.value)} placeholder="Card number" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={19} />
                    <div className="grid grid-cols-2 gap-3">
                      <input value={cardExp} onChange={e => setCardExp(e.target.value)} placeholder="MM/YY" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={5} />
                      <input value={cardCvv} onChange={e => setCardCvv(e.target.value)} placeholder="CVV" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={4} />
                    </div>
                  </div>
                )}
                {payMethod === "paypal" && (
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <i className="ri-paypal-line text-3xl text-amber-500 mb-2 block"></i>
                    <p className="text-sm text-gray-600">You will be redirected to PayPal to complete payment.</p>
                  </div>
                )}
              </div>

              {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-3">{error}</p>}

              <button type="submit" className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base">
                <i className="ri-lock-line mr-2"></i>Place Order — ${total.toFixed(2)}
              </button>
              <p className="text-center text-xs text-gray-400 mt-2">Secured by SSL encryption · Payment held until you confirm receipt</p>
            </form>
          )}

          {/* Success — order placed, awaiting delivery */}
          {step === "success" && (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
              </div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">Order Placed!</h3>
              <p className="text-gray-500 text-sm mb-5">Your sample order is confirmed. The seller will ship soon.</p>

              {/* Tracking */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 w-full mb-4 text-left">
                <p className="text-xs text-amber-600 font-semibold mb-1">UPS Tracking Number</p>
                <p className="text-base font-extrabold text-gray-900 font-mono tracking-wider">{trackingNumber}</p>
                <p className="text-xs text-gray-400 mt-1">Track at ups.com · Delivery: {shipping.days}</p>
              </div>

              {/* Payment held notice */}
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 w-full mb-5 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-shield-check-line text-sky-500"></i>
                  <span className="text-sm font-bold text-sky-700">Payment Protected</span>
                </div>
                <p className="text-xs text-sky-600 leading-relaxed">
                  Your payment of <strong>${total.toFixed(2)}</strong> is securely held. It will only be released to the seller <strong>after you confirm receipt</strong> of your order.
                </p>
              </div>

              {/* Steps */}
              <div className="bg-gray-50 rounded-xl p-4 w-full mb-5 text-left">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">What happens next?</p>
                <ul className="flex flex-col gap-2.5">
                  {[
                    { icon: "ri-store-2-line", text: "Seller prepares and ships your order", done: true },
                    { icon: "ri-truck-line", text: `UPS delivers to ${city || "your address"} in ${shipping.days}`, done: false },
                    { icon: "ri-checkbox-circle-line", text: "You confirm receipt → payment released to seller", done: false },
                    { icon: "ri-message-3-line", text: "Message seller anytime with questions", done: false },
                  ].map((s, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                      <i className={`${s.icon} ${s.done ? "text-green-500" : "text-amber-400"} shrink-0`}></i>
                      {s.text}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Confirm Receipt CTA */}
              <div className="w-full bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4">
                <p className="text-xs font-bold text-green-700 mb-1">Received your order?</p>
                <p className="text-xs text-green-600 mb-3">Once your package arrives, click below to confirm receipt and release payment to the seller.</p>
                <button
                  onClick={handleConfirmReceipt}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-checkbox-circle-line mr-2"></i>Confirm Receipt &amp; Release Payment
                </button>
              </div>

              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm cursor-pointer whitespace-nowrap">
                Close — I&apos;ll confirm later
              </button>
            </div>
          )}

          {/* Received — payment released */}
          {step === "received" && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-hand-heart-line text-green-500 text-4xl"></i>
              </div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">Receipt Confirmed!</h3>
              <p className="text-gray-500 text-sm mb-5">Thank you for confirming. Payment has been released to the seller.</p>

              <div className="bg-green-50 border border-green-200 rounded-xl p-5 w-full mb-5 text-left">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-shield-check-fill text-green-500 text-lg"></i>
                  <span className="font-bold text-green-700">Payment Released</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Amount released</span>
                  <span className="font-bold text-green-600">${total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Seller</span>
                  <span className="font-semibold text-gray-700">{product.sellerFlag} {product.seller}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Order</span>
                  <span className="font-mono text-xs text-gray-600">{trackingNumber}</span>
                </div>
              </div>

              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 w-full mb-5">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-heart-fill text-rose-400"></i>
                  <span className="text-sm font-bold text-rose-600">Thank you for trading on CrownGlobal!</span>
                </div>
                <p className="text-xs text-rose-500">Consider making a small donation for world infants who need love, milk &amp; clothes.</p>
              </div>

              <button onClick={onClose} className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-10 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
